import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  IconTrash,
  Select,
  AutocompleteMultiple,
  Autocomplete,
  Checkbox,
  IconLoader,
  responseErrorsResolver,
  ResponseError,
  SelectItemProps,
  usePrevious,
} from '@ui/src';
import { PurposeCodeItemTypes } from './types';
import { PurposeCodeSettings } from '../../../hoc/settings/types';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const PurposeCodeItem = ({
  purposeCodesSettings,
  setPurposeCodesSettings,
  purposeCodeItem,
  index,
  onDeletePurposeCodeSettings,
  paymentTypes,
  countries,
  deleteInProgress,
  selectedPurposeCodeSettingId,
  deleteIsAvailable,
  creationErrors,
  creationErrorsList,
  setCreationErrors,
  updateErrors,
  updateErrorsList,
  setUpdateErrors,
}: PurposeCodeItemTypes) => {
  const [values, setValues] = useState<PurposeCodeSettings>();
  const [globalError, setGlobalError] = useState('');
  const [currencies, setCurrencies] = useState<SelectItemProps[]>([]);

  const prevPaymentType = usePrevious(values?.paymentCategoryId);

  const {
    setError,
    formState: { errors },
    clearErrors,
  } = useForm();

  const onChange = (e: any) => {
    clearTimeout(timeout);
    const nextValues = { ...values };
    const { name, value, checked } = e.target;

    if (name === `purposeCodeType-${index}`) {
      nextValues.purposeCodeType = value as string;
    } else if (name === `paymentCategoryId-${index}`) {
      nextValues.paymentCategoryId = value as string;
    } else if (name === `bankCountry-${index}`) {
      nextValues.bankCountry = value as string;
    } else if (name === `currenciesIds-${index}`) {
      nextValues.currenciesIds = value as string[];
    } else if (name === `required-${index}`) {
      nextValues.required = checked;
    }

    setValues(nextValues as PurposeCodeSettings);

    timeout = setTimeout(() => {
      const nextPurposeCodeSettings = purposeCodesSettings?.map(
        (item, valueIndex) => {
          let result = { ...item };

          if (index === valueIndex) {
            result = {
              ...nextValues,
              id: result.id,
            } as PurposeCodeSettings;
          }

          return result;
        },
      );

      setPurposeCodesSettings(nextPurposeCodeSettings);
    }, 700);
  };

  useEffect(() => {
    if (
      prevPaymentType &&
      values?.paymentCategoryId &&
      prevPaymentType !== values.paymentCategoryId &&
      values.currenciesIds?.length
    ) {
      onChange({
        target: {
          name: `currenciesIds-${index}`,
          value: [],
        },
      });
    }
  }, [values?.paymentCategoryId, values?.currenciesIds, prevPaymentType]);

  useEffect(() => {
    if (purposeCodeItem?.id.includes('purposeCode')) {
      if (creationErrors) {
        responseErrorsResolver({
          errors: creationErrors.error,
          setError,
          setGlobalError,
        });
      } else {
        setGlobalError('');
        clearErrors([
          'purposeCodeType',
          'paymentCategoryId',
          'bankCountry',
          'currenciesIds',
        ]);
      }
    }
  }, [creationErrors, purposeCodeItem]);

  useEffect(() => {
    if (!purposeCodeItem?.id.includes('purposeCode')) {
      if (updateErrors) {
        responseErrorsResolver({
          errors: updateErrors.error,
          setError,
          setGlobalError,
        });
      } else {
        setGlobalError('');
        clearErrors([
          'purposeCodeType',
          'paymentCategoryId',
          'bankCountry',
          'currenciesIds',
        ]);
      }
    }
  }, [updateErrors, purposeCodeItem]);

  useEffect(() => {
    if (values?.paymentCategoryId && paymentTypes) {
      const currentPaymentType = paymentTypes?.find(
        (item) => item.id === values.paymentCategoryId,
      );

      if (currentPaymentType) {
        setCurrencies(
          currentPaymentType?.currencies?.edges?.map((item) => ({
            name: `${item.node.name} (${item.node.description})`,
            value: item.node.id,
          })),
        );
      }
    }
  }, [paymentTypes, values?.paymentCategoryId]);

  useEffect(() => {
    if (purposeCodeItem) {
      setValues(purposeCodeItem);
    }
  }, [purposeCodeItem]);

  return (
    <div
      className={cn.purposeCodeItem}
      style={{ zIndex: 20 - index, position: 'relative' }}
    >
      <div className={cn.purposeCodeTitle}>
        <div className={cn.purposeCodeTitleLeft}>{`Rule #${index + 1}`}</div>
        {deleteIsAvailable && (
          <button
            type="button"
            className={cn.removeRuleButton}
            onClick={() => {
              onDeletePurposeCodeSettings(purposeCodeItem?.id as string);

              setGlobalError('');
              clearErrors([
                'purposeCodeType',
                'paymentCategoryId',
                'bankCountry',
                'currenciesIds',
              ]);

              if (purposeCodeItem?.id.includes('purposeCode')) {
                const nextCreationErrorsList = creationErrorsList?.filter(
                  (item) => item.id !== purposeCodeItem?.id,
                );
                setCreationErrors(
                  nextCreationErrorsList as {
                    id: string;
                    error: ResponseError;
                  }[],
                );
              }

              if (!purposeCodeItem?.id.includes('purposeCode')) {
                const nextUpdateErrorsList = updateErrorsList?.filter(
                  (item) => item.id !== purposeCodeItem?.id,
                );
                setUpdateErrors(
                  nextUpdateErrorsList as {
                    id: string;
                    error: ResponseError;
                  }[],
                );
              }
            }}
            disabled={deleteInProgress}
          >
            {deleteInProgress &&
            selectedPurposeCodeSettingId === purposeCodeItem?.id ? (
              <IconLoader width={16} height={16} />
            ) : (
              <IconTrash width={18} height={18} />
            )}
          </button>
        )}
      </div>
      <div className={cn.purposeCodeItemContent}>
        <div className={cn.formGroup}>
          <Select
            name={`purposeCodeType-${index}`}
            value={values?.purposeCodeType}
            title="Purpose Code list"
            placeholder="Select List"
            list={[
              {
                name: 'UK Purpose Codes',
                value: 'UK_PURPOSE_CODES',
              },
              {
                name: 'UAE Purpose Codes',
                value: 'UAE_PURPOSE_CODES',
              },
            ]}
            onChange={onChange}
            error={errors?.purposeCodeType?.message as string}
            fullWidth
          />
        </div>
        <div className={cn.formGroup}>
          <Select
            name={`paymentCategoryId-${index}`}
            value={values?.paymentCategoryId}
            title="Payment type"
            placeholder="Select payment type"
            list={
              paymentTypes?.map((item) => ({
                name: item.name,
                value: item.id,
              })) || []
            }
            onChange={onChange}
            error={errors?.paymentCategoryId?.message as string}
            withClearButton
            fullWidth
          />
        </div>
        <div className={cn.formGroup}>
          <Autocomplete
            name={`bankCountry-${index}`}
            value={values?.bankCountry}
            title="Beneficiary Bank Country"
            placeholder="Select country"
            list={countries || []}
            onChange={(e) =>
              onChange({
                target: {
                  name: `bankCountry-${index}`,
                  value: e.target.value,
                },
              })
            }
            error={errors?.bankCountry?.message as string}
            withClearButton
            fullWidth
          />
        </div>
        <div className={cn.formGroup}>
          <AutocompleteMultiple
            name={`currenciesIds-${index}`}
            value={values?.currenciesIds}
            title="Currencies"
            placeholder="Select currencies"
            list={currencies || []}
            error={errors?.currenciesIds?.message as string}
            onChange={onChange}
            disabled={!values?.paymentCategoryId}
            fullWidth
          />
        </div>
        <div className={cn.formGroup}>
          <Checkbox
            name={`required-${index}`}
            title="Required"
            type="checkbox"
            checked={values?.required}
            onChange={onChange}
          />
        </div>
        {globalError && (
          <div className={cn.purposeCodeGlobalError}>{globalError}</div>
        )}
      </div>
    </div>
  );
};

export default PurposeCodeItem;
